/**
 * Email Templates Page — shadcn dashboard layout
 *
 * List view + Sheet slide-over for create/edit (matches WhatsApp templates pattern).
 */
import React, { useState, useEffect } from 'react';
import { Plus, Mail } from 'lucide-react';
import emailTemplatesApi, { EmailTemplate } from '../services/email-templates-api';
import EmailTemplateManagement from '../components/Email/EmailTemplateManagement';
import EmailTemplateBuilder from '../components/Email/EmailTemplateBuilder';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from '../components/ui/use-toast';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '../components/ui/sheet';

const EmailTemplatesPage: React.FC = () => {
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [showBuilder, setShowBuilder] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<EmailTemplate | null>(null);
  const [templateIdToDelete, setTemplateIdToDelete] = useState<number | null>(null);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const data = await emailTemplatesApi.list(false);
      setTemplates(data);
    } catch (err: any) {
      toast({ title: 'Error', description: err.message || 'Failed to load templates', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void fetchTemplates();
  }, []);

  const handleCreate = () => {
    setEditingTemplate(null);
    setShowBuilder(true);
  };

  const handleEdit = (template: EmailTemplate) => {
    setEditingTemplate(template);
    setShowBuilder(true);
  };

  const handleDeleteRequest = (templateId: number) => setTemplateIdToDelete(templateId);

  const handleDeleteConfirm = async () => {
    if (!templateIdToDelete) return;
    try {
      await emailTemplatesApi.delete(templateIdToDelete);
      void fetchTemplates();
      toast({ title: 'Template deleted', description: 'Email template deleted successfully' });
    } catch (err: any) {
      toast({ title: 'Error', description: err.message || 'Failed to delete template', variant: 'destructive' });
    } finally {
      setTemplateIdToDelete(null);
    }
  };

  const handleSave = async () => {
    setShowBuilder(false);
    setEditingTemplate(null);
    void fetchTemplates();
  };

  const handleCancel = () => {
    setShowBuilder(false);
    setEditingTemplate(null);
  };

  return (
    <div className="flex flex-1 flex-col gap-4 py-4 px-4 md:gap-6 md:py-6 lg:px-6">

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Email Templates</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Manage reusable email template designs
          </p>
        </div>
        <Button onClick={handleCreate} size="sm">
          <Plus className="mr-1.5 h-3.5 w-3.5" />
          Create Template
        </Button>
      </div>

      {loading ? (
        <Card>
          <CardHeader className="pb-0">
            <div className="flex items-center justify-between">
              <Skeleton className="h-4 w-32" />
              <div className="flex gap-2">
                <Skeleton className="h-7 w-24 rounded-full" />
                <Skeleton className="h-7 w-24 rounded-full" />
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-0 pb-2 pt-4">
            <Table>
              <TableHeader>
                <TableRow>
                  {['Name', 'Subject', 'Variables', 'Status', 'Usage', 'Created', ''].map((h, i) => (
                    <TableHead key={i} className={i === 0 ? 'pl-6' : i === 6 ? 'pr-6' : ''}>
                      {h && <Skeleton className="h-3 w-16" />}
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {Array.from({ length: 5 }).map((_, i) => (
                  <TableRow key={i}>
                    <TableCell className="pl-6"><Skeleton className="h-3.5 w-28" /></TableCell>
                    <TableCell><Skeleton className="h-3.5 w-48 max-w-[200px]" /></TableCell>
                    <TableCell><div className="flex gap-1"><Skeleton className="h-5 w-16 rounded-full" /><Skeleton className="h-5 w-16 rounded-full" /></div></TableCell>
                    <TableCell><Skeleton className="h-5 w-16 rounded-full" /></TableCell>
                    <TableCell><Skeleton className="h-3.5 w-8" /></TableCell>
                    <TableCell><Skeleton className="h-3.5 w-20" /></TableCell>
                    <TableCell className="pr-6"><div className="flex justify-end gap-1"><Skeleton className="h-7 w-7 rounded-md" /><Skeleton className="h-7 w-7 rounded-md" /></div></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      ) : (
        <EmailTemplateManagement
          templates={templates}
          onEdit={handleEdit}
          onDelete={handleDeleteRequest}
          onRefresh={fetchTemplates}
          onCreateFirst={handleCreate}
        />
      )}

      <Sheet open={showBuilder} onOpenChange={(open) => { if (!open) handleCancel(); }}>
        <SheetContent side="right" className="flex w-full flex-col gap-0 p-0 sm:max-w-3xl">
          <SheetHeader className="shrink-0 space-y-1.5 border-b border-border px-6 py-3.5 pr-14 text-left">
            <SheetTitle className="flex items-center gap-2">
              <Mail className="h-4 w-4 text-muted-foreground" />
              {editingTemplate ? 'Edit email template' : 'Create email template'}
            </SheetTitle>
            <SheetDescription>
              {editingTemplate
                ? `Update “${editingTemplate.name}” — subject, HTML body, variables, and attachments.`
                : 'Build a reusable design with merge fields for outbound email.'}
            </SheetDescription>
          </SheetHeader>
          <EmailTemplateBuilder
            template={editingTemplate}
            onSave={handleSave}
            onCancel={handleCancel}
            embedded
          />
        </SheetContent>
      </Sheet>

      <AlertDialog
        open={!!templateIdToDelete}
        onOpenChange={(open) => !open && setTemplateIdToDelete(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Template</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this email template? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setTemplateIdToDelete(null)}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={handleDeleteConfirm}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

    </div>
  );
};

export default EmailTemplatesPage;
